import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const ClassModal = ({ isOpen, onClose, courseClass, onClassSaved }) => {
  const [formData, setFormData] = useState({
    name: '',
    course: '',
  });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const isEditing = !!courseClass;

  // Fetch courses for dropdown
  useEffect(() => {
    const fetchCourses = async () => {
      if (!isOpen || !currentInstitution?._id) return;

      setLoadingCourses(true);
      try {
        const response = await api.get('/dashboard/courses', {
          params: {
            institution: currentInstitution._id,
            limit: 100, // Get all courses for dropdown
          },
        });
        setCourses(response.data.courses || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch courses',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [isOpen, currentInstitution, toast]);

  // Initialize form when courseClass changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (courseClass) {
        setFormData({
          name: courseClass.name || '',
          course: courseClass.course?._id || courseClass.course || '',
        });
      } else {
        setFormData({
          name: '',
          course: '',
        });
      }
    }
  }, [courseClass, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Class name is required',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!formData.course) {
      toast({
        title: 'Validation Error',
        description: 'Please select a course',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        course: formData.course,
        institution: currentInstitution._id,
      };

      let response;
      if (isEditing) {
        response = await api.patch(
          `/dashboard/course-classes/${courseClass._id}`,
          payload,
        );

        analytics.track('settings:academic:classes:updated', {
          classId: courseClass._id,
          className: payload.name,
          courseId: payload.course,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });

        toast({
          title: 'Success',
          description: 'Class updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        response = await api.post('/dashboard/course-classes', payload);

        analytics.track('settings:academic:classes:created', {
          classId: response.data.courseClass?._id,
          className: payload.name,
          courseId: payload.course,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });

        toast({
          title: 'Success',
          description: 'Class created successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      onClassSaved(response.data.courseClass);
      handleClose();
    } catch (error) {
      console.error('Error saving class:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          `Failed to ${isEditing ? 'update' : 'create'} class`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form fields only if this is an "add" modal
      // For edit modals, let the useEffect handle repopulation on next open
      if (!courseClass) {
        setFormData({
          name: '',
          course: '',
        });
      }
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditing ? 'Edit Class' : 'Add New Class'}</ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <form onSubmit={handleSubmit}>
          <ModalBody pb={6}>
            <FormControl isRequired mb={4}>
              <FormLabel>Class Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter class name"
                isDisabled={loading}
              />
            </FormControl>

            <FormControl isRequired mb={4}>
              <FormLabel>Course</FormLabel>
              {loadingCourses ? (
                <Flex justifyContent="center" py={4}>
                  <Spinner size="sm" />
                </Flex>
              ) : (
                <Select
                  name="course"
                  value={formData.course}
                  onChange={handleInputChange}
                  placeholder="Select a course"
                  isDisabled={loading}
                >
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg="#EF6506"
              color="white"
              _hover={{ bg: '#D55A05' }}
              isLoading={loading}
              loadingText={isEditing ? 'Updating...' : 'Creating...'}
            >
              {isEditing ? 'Update Class' : 'Create Class'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ClassModal;
